import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ArrowLeft, ImageUp, LoaderCircle, Share2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { REMOVE_PHOTOS_FROM_ALBUM, UPDATE_PHOTO_ALBUM } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoUi } from '../lib/permissions'
import type { PhotoAlbum } from '../types'

interface Props {
  album: PhotoAlbum | undefined
  ui: PhotoUi
  selectedIds: string[]
  reordering: boolean
  onBack: () => void
  onClearSelection: () => void
  onShareAlbum?: (albumId: string) => void
}

/** Toolbar of the album view: back, title, set cover (one selected photo), remove from album. */
const AlbumBar: React.FC<Props> = ({ album, ui, selectedIds, reordering, onBack, onClearSelection, onShareAlbum }) => {
  const { t } = useTranslation()
  const [setCover, { loading: settingCover }] = useMutation(UPDATE_PHOTO_ALBUM, { context: { handlesErrors: true }, refetchQueries: ['photoAlbums'] })
  const [removePhotos, { loading: removing }] = useMutation(REMOVE_PHOTOS_FROM_ALBUM, {
    context: { handlesErrors: true },
    refetchQueries: ['photoAlbums', 'photos'],
  })
  const busy = settingCover || removing || reordering

  const run = async (fn: () => Promise<unknown>, successKey: string) => {
    try {
      await fn()
      toastr.success('', t(successKey, { count: selectedIds.length }))
      onClearSelection()
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 pb-2">
      <Button variant="outline" size="sm" className="h-8" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
        {t('Common_back')}
      </Button>
      <h2 className="min-w-0 truncate text-lg font-semibold">{album?.name}</h2>
      {reordering && <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />}
      {album?.canEdit && <span className="text-sm text-muted-foreground">{t('Photos_album_reorder_hint')}</span>}
      <div className="ml-auto flex items-center gap-2">
        {onShareAlbum && album && (
          <Button variant="outline" size="sm" className="h-8" onClick={() => onShareAlbum(album.id)}>
            <Share2 className="h-4 w-4" />
            {t('Photos_share_album')}
          </Button>
        )}
        {album?.canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={busy || selectedIds.length !== 1}
            onClick={() => run(() => setCover({ variables: { id: album.id, coverPhotoId: selectedIds[0] } }), 'Photos_album_cover_set')}
          >
            {settingCover ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />}
            {t('Photos_album_set_cover')}
          </Button>
        )}
        {ui.canManageStructure && album && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={busy || selectedIds.length === 0}
            onClick={() => run(() => removePhotos({ variables: { albumId: album.id, photoIds: selectedIds } }), 'Photos_album_removed')}
          >
            {removing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t('Photos_album_remove')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default AlbumBar
